import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { onContactUpdate } from '@rsmaids/shared/services/api/contacts';

/**
 * Service for enhanced unified inbox operations
 * Handles customer lookups, grouping, and non-contact call handling
 */

interface CustomerInfo {
  id: string;
  name: string;
  avatar?: string;
  phone?: string;
  email?: string;
}

// Cache for customer lookups to avoid repeated queries
const customerCache = new Map<string, CustomerInfo>();
const phoneToCustomerCache = new Map<string, CustomerInfo>();

// Event listeners for cache invalidation
type CacheInvalidationListener = () => void;
const cacheInvalidationListeners = new Set<CacheInvalidationListener>();

/**
 * Subscribe to cache invalidation events
 * Called when a contact is created/updated and cache needs to refresh
 */
export function onCacheInvalidation(listener: CacheInvalidationListener): () => void {
  cacheInvalidationListeners.add(listener);
  // Return unsubscribe function
  return () => {
    cacheInvalidationListeners.delete(listener);
  };
}

/**
 * Notify all listeners that cache has been invalidated
 */
function notifyCacheInvalidation(): void {
  console.log(`🔄 Cache invalidated, notifying ${cacheInvalidationListeners.size} listeners`);
  cacheInvalidationListeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      console.error('Error in cache invalidation listener:', error);
    }
  });
}

// 📬 Wire contact update events to cache invalidation
onContactUpdate((event: { contactId: string; phone?: string }) => {
  console.log(`📬 Contact updated from DataService: ${event.contactId}`);
  invalidateContactCache(event.contactId, event.phone);
});

/**
 * Invalidate cache for a specific contact
 * Called when contact is created or updated
 */
export function invalidateContactCache(contactId: string, phoneNumber?: string): void {
  console.log(`🗑️ Invalidating cache for contact ${contactId}${phoneNumber ? ` (${phoneNumber})` : ''}`);
  
  // Remove from ID cache
  if (customerCache.has(contactId)) {
    customerCache.delete(contactId);
    console.log(`  ✅ Removed from ID cache: ${contactId}`);
  }
  
  // Remove from phone cache
  if (phoneNumber && phoneToCustomerCache.has(phoneNumber)) {
    phoneToCustomerCache.delete(phoneNumber);
    console.log(`  ✅ Removed from phone cache: ${phoneNumber}`);
  }
  
  // Also remove generic "Unknown" entry for this phone
  if (phoneNumber) {
    const genericKey = `phone-${phoneNumber}`;
    if (phoneToCustomerCache.has(phoneNumber)) {
      phoneToCustomerCache.delete(phoneNumber);
    }
    if (phoneToCustomerCache.has(genericKey)) {
      phoneToCustomerCache.delete(genericKey);
      console.log(`  ✅ Removed generic key: ${genericKey}`);
    }
  }
  
  // Notify UI to refresh
  notifyCacheInvalidation();
}

/**
 * Look up customer by ID
 * Returns cached result if available
 */
// Customer ids with no contact (deleted, or Wix system members such as "Wix Legend"). Remembered for the session so the
// Inbox does not repeat the failing lookup — and log it — on every render.
const missingCustomerIds = new Set<string>();

export async function getCustomerById(customerId: string): Promise<CustomerInfo | null> {
  if (customerCache.has(customerId)) {
    return customerCache.get(customerId) || null;
  }
  if (missingCustomerIds.has(customerId)) return null;

  try {
    const dataService = DataServiceFactory.getService();
    const contact = await dataService.contacts.getContactById(customerId);
    
    if (contact) {
      const primaryPhone = contact.phones?.find(p => p.primary)?.phone || contact.phones?.[0]?.phone;
      const primaryEmail = contact.emails?.find(e => e.primary)?.email || contact.emails?.[0]?.email;
      
      const customerInfo: CustomerInfo = {
        id: contact.id,
        name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown',
        avatar: contact.avatar,
        phone: primaryPhone,
        email: primaryEmail
      };
      customerCache.set(customerId, customerInfo);
      return customerInfo;
    }
  } catch (error) {
    if ((error as { code?: string })?.code === 'CONTACT_NOT_FOUND') {
      missingCustomerIds.add(customerId); // expected for contacts that no longer exist — not an error
    } else {
      console.error(`Failed to lookup customer ${customerId}:`, error);
    }
  }

  return null;
}

/**
 * Normalizes a phone number down to its bare 10-digit (US) or full digit
 * string, so numbers stored with different formatting (+1, dashes,
 * parens, spaces) can still be matched against each other.
 */
function phoneDigits(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/**
 * Look up customer by phone number
 * Used for non-contact calls/SMS
 * Returns cached result if available
 */
export async function getCustomerByPhone(phoneNumber: string): Promise<CustomerInfo | null> {
  if (phoneToCustomerCache.has(phoneNumber)) {
    return phoneToCustomerCache.get(phoneNumber) || null;
  }

  try {
    const dataService = DataServiceFactory.getService();
    const allContacts = await dataService.contacts.getAllContacts();

    if (allContacts && Array.isArray(allContacts)) {
      const targetDigits = phoneDigits(phoneNumber);
      const contact = allContacts.find(c =>
        c.primaryPhone && phoneDigits(c.primaryPhone) === targetDigits
      );
      
      if (contact) {
        const customerInfo: CustomerInfo = {
          id: contact.id,
          name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.displayName || phoneNumber,
          avatar: contact.avatar,
          phone: phoneNumber,
          email: contact.primaryEmail
        };
        phoneToCustomerCache.set(phoneNumber, customerInfo);
        return customerInfo;
      }
    }
  } catch (error) {
    console.error(`Failed to lookup customer by phone ${phoneNumber}:`, error);
  }

  // Return generic info for non-contact calls
  const genericInfo: CustomerInfo = {
    id: `phone-${phoneNumber}`,
    name: `Unknown (${phoneNumber})`,
    phone: phoneNumber
  };
  phoneToCustomerCache.set(phoneNumber, genericInfo);
  return genericInfo;
}

/**
 * Clear entire cache (useful after bulk updates)
 */
export function clearCustomerCache(): void {
  console.log('🗑️ Clearing entire customer cache');
  customerCache.clear();
  phoneToCustomerCache.clear();
  missingCustomerIds.clear();
  notifyCacheInvalidation();
}

/**
 * Get customer info by ID or phone
 * Tries ID first, falls back to phone lookup
 */
export async function getCustomerInfo(
  customerId: string | undefined,
  phoneNumber: string | undefined
): Promise<CustomerInfo | null> {
  if (customerId) {
    const byId = await getCustomerById(customerId);
    if (byId) return byId;
  }

  if (phoneNumber) {
    return getCustomerByPhone(phoneNumber);
  }

  return null;
}
